
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/contexts/TenantContext";

type Branding = {
  host: string;
  tenant_id: string | null;
  display_name: string;
  header_logo_url: string;
  header_text?: string;
  header_style?: 'logo' | 'avatar';
  favicon_url: string;
  hero_title: string;
  hero_subtitle: string;
  hero_description: string;
  hero_image_url: string;
  primary_color: string;
  // NOVOS CAMPOS DA HERO SECTION
  hero_primary_button_text: string;
  hero_primary_button_action: string;
  hero_secondary_button_text: string;
  hero_secondary_button_action: string;
  hero_trust_indicator_1: string;
  hero_trust_indicator_2: string;
  seo_title?: string;
  seo_description?: string;
  property_card_style?: 'modern' | 'classic' | 'minimalist';
  watermark_enabled?: boolean;
  watermark_url?: string;
  watermark_position?: string;
  watermark_opacity?: number;
} | null;

type BrandingContextType = {
  branding: Branding;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
};

const BrandingContext = createContext<BrandingContextType | undefined>(undefined);

const DEFAULT_BRAND: NonNullable<Branding> = {
  host: "",
  tenant_id: null,
  display_name: "ImobPanda",
  header_logo_url: "",
  favicon_url: "",
  hero_title: "Seu imóvel dos sonhos está aqui",
  hero_subtitle: "Seu portal imobiliário moderno",
  hero_description: "Encontre seu lar perfeito com nossa curadoria exclusiva",
  hero_image_url: "",
  primary_color: "#0EA5E9",
  // NOVOS CAMPOS DA HERO SECTION
  hero_primary_button_text: "Ver Imóveis",
  hero_primary_button_action: "scroll_to_properties",
  hero_secondary_button_text: "Fale Conosco",
  hero_secondary_button_action: "contact",
  hero_trust_indicator_1: "Confiança & Transparência",
  hero_trust_indicator_2: "Excelência & Profissionalismo",
  seo_title: "",
  seo_description: "",
  property_card_style: "modern",
  header_style: "logo",
  watermark_enabled: false,
  watermark_url: "",
  watermark_position: "center",
  watermark_opacity: 0.5,
};

export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const { tenant, loading: tenantLoading } = useTenant();
  const [branding, setBranding] = useState<Branding>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const applyCssVars = (b: Branding) => {
    const root = document.documentElement;
    const primary = b?.primary_color || DEFAULT_BRAND.primary_color;

    console.log("[BrandingProvider] Applying CSS vars:", { primary });

    root.style.setProperty("--brand-primary", primary);
    // Set secondary to primary as fallback (since secondary_color was removed)
    root.style.setProperty("--brand-secondary", primary);

    // Update document title
    const title = b?.seo_title || b?.display_name || DEFAULT_BRAND.display_name;
    if (title) {
      document.title = title;
    }

    // Update meta description
    const description = b?.seo_description;
    if (description) {
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) {
        metaDesc = document.createElement('meta');
        metaDesc.setAttribute('name', 'description');
        document.head.appendChild(metaDesc);
      }
      metaDesc.setAttribute('content', description);
    }
  };

  const applyFavicon = (b: Branding) => {
    const faviconUrl = b?.favicon_url;

    console.log("[BrandingProvider] Applying favicon:", faviconUrl);

    // Remove existing favicon links
    const existingFavicons = document.querySelectorAll('link[rel*="icon"]');
    existingFavicons.forEach(link => link.remove());

    if (faviconUrl) {
      // Add standard favicon
      const link = document.createElement('link');
      link.rel = 'icon';
      link.type = 'image/x-icon';
      link.href = faviconUrl;
      document.head.appendChild(link);

      // Add shortcut icon (for older browsers)
      const shortcutLink = document.createElement('link');
      shortcutLink.rel = 'shortcut icon';
      shortcutLink.type = 'image/x-icon';
      shortcutLink.href = faviconUrl;
      document.head.appendChild(shortcutLink);

      // Add Apple touch icon
      const appleLink = document.createElement('link');
      appleLink.rel = 'apple-touch-icon';
      appleLink.href = faviconUrl;
      document.head.appendChild(appleLink);

      // Add 32x32 favicon
      const icon32Link = document.createElement('link');
      icon32Link.rel = 'icon';
      icon32Link.type = 'image/png';
      icon32Link.sizes = '32x32';
      icon32Link.href = faviconUrl;
      document.head.appendChild(icon32Link);

      // Add 16x16 favicon
      const icon16Link = document.createElement('link');
      icon16Link.rel = 'icon';
      icon16Link.type = 'image/png';
      icon16Link.sizes = '16x16';
      icon16Link.href = faviconUrl;
      document.head.appendChild(icon16Link);

      console.log("[BrandingProvider] Favicon applied successfully");
    } else {
      console.log("[BrandingProvider] No favicon URL provided, using default");
    }
  };

  const loadBranding = async () => {
    setLoading(true);
    setError(null);

    try {
      const host = window.location.host;
      console.log("[BrandingProvider] Loading branding for tenant:", tenant?.id, "host:", host);

      let data = null;
      let qError = null;

      // SIMPLIFIED LOGIC: Always use public_tenant_settings for consistency
      // This ensures logged and public users see the same data

      if (tenant?.id) {
        console.log("[BrandingProvider] Loading branding from public_tenant_settings for tenant:", tenant.id);

        const { data: publicSettings, error: publicError } = await supabase
          .from("public_tenant_settings")
          .select("*")
          .eq("tenant_id", tenant.id)
          .maybeSingle();

        data = publicSettings;
        qError = publicError;

        console.log("[BrandingProvider] Public settings data:", data);
      } else {
        console.log("[BrandingProvider] No tenant from context, trying hostname-based lookup");

        // Try direct lookup by host in public_tenant_settings
        const { data: hostSettings, error: hostError } = await supabase
          .from("public_tenant_settings")
          .select("*")
          .eq("host", host)
          .maybeSingle();

        data = hostSettings;
        qError = hostError;

        console.log("[BrandingProvider] Host-based data:", data);
      }

      console.log("[BrandingProvider] Final data:", { data, error: qError });

      if (qError) {
        console.error("[BrandingProvider] Error loading branding:", qError);
        setError("Não foi possível carregar o tema");
        setBranding(DEFAULT_BRAND);
        applyCssVars(DEFAULT_BRAND);
      } else {
        const parsed: Branding = data
          ? {
            host: data.host,
            tenant_id: data.tenant_id ?? null,
            display_name: data.display_name || DEFAULT_BRAND.display_name,
            header_logo_url: data.header_logo_url || "",
            header_text: data.header_text || "",
            header_style: data.header_style || "logo",
            favicon_url: data.favicon_url || "",
            hero_title: data.hero_title || DEFAULT_BRAND.hero_title,
            hero_subtitle: data.hero_subtitle || DEFAULT_BRAND.hero_subtitle,
            hero_description: data.hero_description || DEFAULT_BRAND.hero_description,
            hero_image_url: data.hero_image_url || "",
            primary_color: data.primary_color || DEFAULT_BRAND.primary_color,
            // NOVOS CAMPOS DA HERO SECTION
            hero_primary_button_text: data.hero_primary_button_text || DEFAULT_BRAND.hero_primary_button_text,
            hero_primary_button_action: data.hero_primary_button_action || DEFAULT_BRAND.hero_primary_button_action,
            hero_secondary_button_text: data.hero_secondary_button_text || DEFAULT_BRAND.hero_secondary_button_text,
            hero_secondary_button_action: data.hero_secondary_button_action || DEFAULT_BRAND.hero_secondary_button_action,
            hero_trust_indicator_1: data.hero_trust_indicator_1 || DEFAULT_BRAND.hero_trust_indicator_1,
            hero_trust_indicator_2: data.hero_trust_indicator_2 || DEFAULT_BRAND.hero_trust_indicator_2,
            seo_title: data.seo_title || "",
            seo_description: data.seo_description || "",
            property_card_style: data.property_card_style || "modern",
            watermark_enabled: data.watermark_enabled ?? DEFAULT_BRAND.watermark_enabled,
            watermark_url: data.watermark_url || DEFAULT_BRAND.watermark_url,
            watermark_position: data.watermark_position || DEFAULT_BRAND.watermark_position,
            watermark_opacity: data.watermark_opacity ?? DEFAULT_BRAND.watermark_opacity,
          }
          : DEFAULT_BRAND;

        console.log("[BrandingProvider] Parsed branding:", parsed);
        setBranding(parsed);
        applyCssVars(parsed);
        applyFavicon(parsed);
      }
    } catch (err) {
      console.error("[BrandingProvider] Unexpected error:", err);
      setError("Erro inesperado ao carregar o tema");
      setBranding(DEFAULT_BRAND);
      applyCssVars(DEFAULT_BRAND);
    }

    setLoading(false);
  };

  useEffect(() => {
    if (!tenantLoading) {
      void loadBranding();
    }

    // Listen for storage events for cross-tab refresh when saving in admin
    const onStorage = (e: StorageEvent) => {
      if (e.key === "branding_refresh") {
        console.log("[BrandingProvider] Storage event detected, refreshing branding");
        void loadBranding();
      }
    };

    // Listen for custom events for same-tab refresh
    const onCustomEvent = () => {
      console.log("[BrandingProvider] Custom event detected, refreshing branding");
      void loadBranding();
    };

    window.addEventListener("storage", onStorage);
    window.addEventListener("branding_updated", onCustomEvent);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("branding_updated", onCustomEvent);
    };
  }, [tenantLoading, tenant?.id]);

  const value = useMemo<BrandingContextType>(
    () => ({
      branding,
      loading,
      error,
      refresh: loadBranding,
    }),
    [branding, loading, error]
  );

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

export function useBranding() {
  const ctx = useContext(BrandingContext);
  if (!ctx) throw new Error("useBranding must be used within a BrandingProvider");
  return ctx;
}
